import { useCallback, useEffect, useRef, useState } from 'react'
import { Download, Loader2, LogOut, Upload } from 'lucide-react'

import { useAuth } from '../../auth/authContext'
import { Field } from '../../components/Field'
import { alleBestaende, dirtyDocs, failedBlobs, pendingBlobs } from '../../data/db'
import { useRoadbook } from '../../data/roadbookContext'
import { anzahl, zeile } from '../../lib/format'
import { ImportError, parseBundle } from '../../lib/transfer'
import { fahrzeug, FUEL_UNIT } from '../../lib/vehicle'
import { NavExport } from './NavExport'
import { VehicleEditor } from './VehicleEditor'

/** Wie lange vor dem Abmelden höchstens auf den letzten Abgleich gewartet wird. */
const ABGLEICH_WARTEN_MS = 10_000

/** Was ein Abmelden auf diesem Gerät vernichten würde. */
export interface OffeneArbeit {
  /** Änderungen, die der Server noch nicht hat: Dokumente und wartende Fotos. */
  offen: number
  /** Fotos, die der Server dauerhaft abgelehnt hat. Sie liegen nur noch hier. */
  abgelehnt: number
}

/**
 * Zählt aus der lokalen Datenbank, was noch nicht beim Server ist.
 *
 * Bewusst nicht aus dem Abgleichzustand: dessen pending zählt nur die
 * SyncEngine am Ende eines Durchgangs neu, und die laeuft ausschliesslich im
 * Zustand ready. Wer stundenlang offline Tagebuch schreibt, hätte dort weiter
 * eine 0 stehen - und beim Abmelden nur die harmlose Rückfrage gesehen,
 * während das Löschen die ganze Arbeit mitnimmt.
 *
 * @param tripId eine einzelne Reise, oder nichts für alle Bestände auf
 *   diesem Gerät. Die Rückfrage vor dem Abmelden braucht die zweite Form: das
 *   Löschen nimmt alle Reisen mit, auch die gerade nicht offene, und eine
 *   Zählung nur über die offene verschwiege genau die Änderungen, an die im
 *   Moment niemand denkt.
 */
async function zaehleOffen(tripId?: string): Promise<OffeneArbeit> {
  const ids = tripId === undefined ? await alleBestaende() : [tripId]
  const summe: OffeneArbeit = { offen: 0, abgelehnt: 0 }
  for (const id of ids) {
    const [docs, blobs, abgelehnt] = await Promise.all([
      dirtyDocs(id),
      pendingBlobs(id),
      failedBlobs(id),
    ])
    summe.offen += docs.length + blobs.length
    summe.abgelehnt += abgelehnt.length
  }
  return summe
}

/*
 * Die beiden folgenden Funktionen sind rein und deshalb ohne DOM prüfbar,
 * siehe ../../auth/abmelden.test.ts. Dass Fast Refresh dafür in dieser Datei
 * aussetzt, ist der günstigere Tausch: der Weg vor dem Abmelden entscheidet
 * über unwiederbringliche Daten und gehört unter Test.
 */

/**
 * Formuliert die Rückfrage vor dem Abmelden.
 *
 * Dauerhaft abgelehnte Fotos zählen ausdrücklich mit: der Abgleich versucht
 * sie nie wieder, sie stehen also in keiner Warteschlange - verloren sind sie
 * beim Abmelden trotzdem.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function abmeldeFrage({ offen, abgelehnt }: OffeneArbeit): string {
  if (offen === 0 && abgelehnt === 0) {
    return 'Abmelden? Der lokale Bestand auf diesem Gerät wird gelöscht. Alles Abgeglichene kommt bei der nächsten Anmeldung zurück.'
  }

  const teile: string[] = []
  if (offen > 0) {
    teile.push(
      offen === 1
        ? '1 Änderung ist noch nicht mit dem Server abgeglichen'
        : `${offen} Änderungen sind noch nicht mit dem Server abgeglichen`,
    )
  }
  if (abgelehnt > 0) {
    teile.push(
      abgelehnt === 1
        ? '1 Foto hat der Server dauerhaft abgelehnt'
        : `${abgelehnt} Fotos hat der Server dauerhaft abgelehnt`,
    )
  }

  return (
    `${teile.join(' und ')}. ` +
    'Das Abmelden löscht den lokalen Bestand auf diesem Gerät, danach ist das unwiederbringlich weg. ' +
    'Brich ab und lege zuerst einen Export an, wenn du es behalten willst. Trotzdem abmelden?'
  )
}

/**
 * Ob eine Ausnahme daran liegt, dass die Datei für den Speicher zu gross war.
 *
 * Eine Zeichenkette hat im Browser eine Obergrenze, und eine Exportdatei mit
 * hundert Fotos kommt ihr nahe. Der Fehler heisst dann "Invalid string length"
 * oder es geht schlicht der Speicher aus - beides ist keine kaputte Datei, und
 * die übliche Meldung über ein unbekanntes Format wäre hier eine falsche
 * Auskunft, die den Nutzer nach dem Fehler in seinen Daten suchen lässt.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function zuGrossFuerDenSpeicher(err: unknown): boolean {
  if (err instanceof RangeError) return true
  return err instanceof Error && /string length|out of memory|allocation/i.test(err.message)
}

/**
 * Wartet auf eine Aufgabe, aber höchstens ms Millisekunden.
 *
 * @param aufgabe worauf gewartet wird.
 * @param ms Obergrenze der Wartezeit.
 *
 * Erfüllt sich, sobald die Aufgabe durch ist - oder sobald die Obergrenze
 * erreicht ist, je nachdem was zuerst kommt. Die Obergrenze ist kein Fehler:
 * der Aufrufer macht danach mit dem weiter, was vorliegt.
 *
 * Eine abgelehnte Aufgabe zählt wie eine erledigte. Das blosse Warten soll
 * nichts weiterreichen, was den Aufrufer in seinen eigenen Fehlerpfad zwingt.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function mitObergrenze(aufgabe: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((fertig) => {
    const uhr = setTimeout(fertig, ms)
    const durch = () => {
      // Sonst hält der Zeitgeber die Wartezeit über das Ende der Aufgabe
      // hinaus am Leben, ohne dass noch jemand auf ihn hört.
      clearTimeout(uhr)
      fertig()
    }
    aufgabe.then(durch, durch)
  })
}

/**
 * Ein Währungscode nach ISO 4217: drei Buchstaben, gross.
 *
 * Streng, weil die Anzeige daran hängt: Intl kennt nur Codes dieser Form, und
 * alles andere käme als Ausnahme mitten in der Budgetansicht heraus. money()
 * fängt das inzwischen ab (siehe lib/format.ts), aber ein Feld, das nur
 * Brauchbares durchlässt, ist die bessere Hälfte davon.
 */
function waehrungscode(roh: string): string {
  return roh
    .replace(/[^a-zA-Z]/g, '')
    .toUpperCase()
    .slice(0, 3)
}

export function DataView() {
  const { trip, darfSchreiben, setMeta, buildExport, importBundle, sync, syncNow, wipeForSignOut } =
    useRoadbook()
  const { signOut, config, displayName, status } = useAuth()
  /** Liter oder Kilowattstunden - was dieses Fahrzeug bezahlt. */
  const einheit = FUEL_UNIT[fahrzeug(trip).fuel]

  const [meldung, setMeldung] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [gleichtAb, setGleichtAb] = useState(false)
  /** Wie viele Fotos der laufende Export schon verpackt hat, und wie viele es sind. */
  const [packt, setPackt] = useState<{ fertig: number; gesamt: number } | null>(null)
  const [arbeit, setArbeit] = useState<OffeneArbeit>({ offen: 0, abgelehnt: 0 })
  const dateiFeld = useRef<HTMLInputElement>(null)

  // Solange die Ansicht steht. Danach darf nichts mehr angezeigt werden, was
  // sich auf einen längst verlassenen Reiter bezieht.
  const sichtbar = useRef(true)
  useEffect(() => {
    sichtbar.current = true
    return () => {
      sichtbar.current = false
    }
  }, [])

  const neuZaehlen = useCallback(async () => {
    try {
      // Über alle Bestände: der Hinweis steht beim Abmelden, und das nimmt
      // sie alle mit.
      const jetzt = await zaehleOffen()
      if (sichtbar.current) setArbeit(jetzt)
    } catch (err) {
      console.warn('Offene Änderungen liessen sich nicht zählen', err)
    }
  }, [])

  // Einmal beim Öffnen des Reiters, danach jedes Mal, wenn der Abgleich etwas
  // gemeldet hat - und zusätzlich bei jeder Änderung an den Reisedaten.
  //
  // Das letzte Stück ist nötig, weil im Zustand offline gar keine
  // Abgleich-Maschine laeuft: dort ändert sich keiner der sync-Werte, und die
  // Zahl unter dem Knopf bliebe stehen, während hier oben getippt wird. trip
  // ist der einzige Bestand, der sich in diesem Reiter überhaupt ändern
  // lässt, und seine Identität wechselt bei jedem Schreibvorgang. Was in den
  // anderen Reitern entsteht, deckt das Öffnen ab: die App zeigt immer nur
  // einen Reiter, dieser hier wird beim Hinwechseln neu aufgebaut.
  //
  // Eine Dauerschleife auf der Datenbank wäre für eine Zeile Hinweistext
  // weiterhin zu viel des Guten.
  useEffect(() => {
    void neuZaehlen()
  }, [neuZaehlen, trip, sync.state, sync.lastSync, sync.pending, sync.failedUploads])

  /**
   * Versucht vor dem Abmelden einen letzten Abgleich und liefert, was danach
   * noch auf dem Gerät liegt.
   *
   * syncNow ist erst erfüllt, wenn der Durchgang wirklich durch und der Stand
   * geschrieben ist; abgelehnt wird es nicht, seine Fehler meldet die
   * Datenschicht über den Abgleichzustand. Gewartet wird deshalb auf dieses
   * Versprechen und nicht mehr auf einen Zustand im Takt: ein Durchgang, der
   * zwischen zwei Blicken fertig wurde, liess den Knopf sonst die vollen
   * ABGLEICH_WARTEN_MS stehen, obwohl längst alles gesendet war.
   *
   * Die Obergrenze bleibt trotzdem: im Funkloch laeuft ein Durchgang bis in
   * seine eigenen Zeitgrenzen, und so lange darf der Knopf nicht hängen. Wird
   * sie erreicht, ist das kein Fehler - dann wird eben gezählt, was vorliegt.
   *
   * @returns die offene Arbeit, oder null wenn die Ansicht inzwischen weg ist.
   */
  async function letzterAbgleich(): Promise<OffeneArbeit | null> {
    const vorher = await zaehleOffen()
    // Nichts zu senden, oder ohne bestehende Anmeldung laeuft ohnehin keine
    // Abgleich-Maschine: dann wäre das Warten nur Zeitverlust.
    //
    // Der letzte Abgleich betrifft dabei nur die offene Reise - für die
    // anderen laeuft keine Maschine. Gezählt wird trotzdem über alles: die
    // Rückfrage muss sagen, was verloren geht, und nicht, was noch zu retten
    // war.
    if (vorher.offen === 0 || status !== 'ready') return vorher

    await mitObergrenze(syncNow(), ABGLEICH_WARTEN_MS)
    if (!sichtbar.current) return null

    // Wieder frisch aus der Datenbank: der Zähler im Abgleichzustand kennt nur
    // den Stand vom Ende des Durchgangs, nicht das währenddessen Getippte.
    // Ohne Reise-ID: alles auf dem Gerät, denn genau das löscht das Abmelden.
    const nachher = await zaehleOffen()
    // Und danach noch einmal nachgesehen: das Zählen ist der letzte
    // Wartepunkt, und wer die Ansicht genau hier verlässt, bekäme sonst eine
    // Rückfrage vorgesetzt, der man nicht mehr ansieht, worauf sie sich
    // bezieht.
    if (!sichtbar.current) return null
    return nachher
  }

  async function abmelden() {
    setLaeuft(true)
    setGleichtAb(true)
    setMeldung(null)
    setFehler(null)
    try {
      const offen = await letzterAbgleich()
      setGleichtAb(false)
      // Die Ansicht ist weg: eine Rückfrage wäre jetzt völlig kontextlos.
      if (!offen) return
      setArbeit(offen)
      if (!window.confirm(abmeldeFrage(offen))) return
      // Erst der lokale Bestand, dann der Anmeldedienst - und in dieser
      // Reihenfolge, weil signOut zu Authentik umleitet: was danach noch
      // anfängt, kommt nicht mehr zum Löschen. wipeForSignOut legt den
      // Abgleich still, wartet den laufenden Durchgang ab und kehrt erst
      // zurück, wenn nichts mehr auf dem Gerät liegt.
      await wipeForSignOut()
      await signOut()
    } catch (err) {
      console.error('Abmelden fehlgeschlagen', err)
      if (sichtbar.current) setFehler('Das Abmelden ist fehlgeschlagen.')
    } finally {
      if (sichtbar.current) {
        setGleichtAb(false)
        setLaeuft(false)
      }
    }
  }

  async function exportieren() {
    setLaeuft(true)
    setMeldung(null)
    setFehler(null)
    setPackt({ fertig: 0, gesamt: 0 })
    let fotos = 0
    try {
      // Der Export packt die Fotos einzeln und meldet nach jedem, wie weit er
      // ist. Bei hundert Bildern dauert das eine Minute, und ohne Anzeige sieht
      // das aus wie eine hängende App.
      const blob = await buildExport((fertig, gesamt) => {
        fotos = gesamt
        if (sichtbar.current) setPackt({ fertig, gesamt })
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `roadbook-${new Date().toISOString().slice(0, 10)}.json`
      link.click()
      URL.revokeObjectURL(url)
      if (sichtbar.current) {
        setMeldung(
          fotos === 1 ? 'Export erstellt, mit 1 Foto.' : `Export erstellt, mit ${fotos} Fotos.`,
        )
      }
    } catch (err) {
      console.error('Export fehlgeschlagen', err)
      if (sichtbar.current) setFehler('Der Export ist fehlgeschlagen.')
    } finally {
      if (sichtbar.current) {
        setPackt(null)
        setLaeuft(false)
      }
    }
  }

  async function importieren(file: File) {
    if (
      !window.confirm(
        'Der Import ersetzt den gesamten Bestand auf diesem Gerät und danach auch auf dem Server. Fortfahren?',
      )
    ) {
      return
    }

    setLaeuft(true)
    setMeldung(null)
    setFehler(null)
    try {
      const bundle = parseBundle(JSON.parse(await file.text()))
      await importBundle(bundle)
      setMeldung(
        `Übernommen: ${bundle.trip.stages.length} Etappen, ${bundle.diary.length} Tagebucheinträge, ${bundle.posts.length} Beiträge, ${bundle.expenses.length} Ausgaben, ${bundle.photos.length} Fotos.`,
      )
      // Der Import hat den ganzen Bestand ersetzt, der Hinweis unten stimmt so
      // nicht mehr.
      await neuZaehlen()
    } catch (err) {
      console.error('Import fehlgeschlagen', err)
      setFehler(
        err instanceof ImportError
          ? err.message
          : zuGrossFuerDenSpeicher(err)
            ? 'Die Datei ist zu gross für den Speicher dieses Geräts. Der Import muss die ganze Datei auf einmal lesen; auf einem Rechner mit mehr Speicher geht sie durch, und von dort kommt sie über den Abgleich wieder auf das Telefon.'
            : 'Die Datei liess sich nicht lesen. Erwartet wird ein Roadbook-Export im JSON-Format.',
      )
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <section className="seite daten">
      <div className="block">
        <h4>Reise</h4>
        {/*
          Ohne Schreibrecht stehen dieselben Angaben als Satz da. Felder, die
          sich anfassen lassen und deren Inhalt gleich wieder verschwindet,
          wären schlimmer als gar keine.
        */}
        {!darfSchreiben && (
          <p>
            {/* Was nicht eingetragen ist, steht auch nicht da - sonst las sich
                eine frisch angelegte Reise als " · · Abfahrt · 1 Personen · 0
                EUR je Liter". Das Fahrzeug steht im Block darunter. */}
            {zeile([
              trip.meta.title,
              trip.meta.start && `Abfahrt ${trip.meta.start}`,
              anzahl(trip.meta.crew, 'Person', 'Personen'),
              trip.meta.fuelPrice > 0 &&
                `${String(trip.meta.fuelPrice)} ${trip.meta.currency} je ${einheit}`,
            ]) || 'Zu dieser Reise ist noch nichts eingetragen.'}
          </p>
        )}
        {darfSchreiben && (
          <div className="raster-2">
            <Field label="Titel">
              <input value={trip.meta.title} onChange={(e) => setMeta({ title: e.target.value })} />
            </Field>
            <Field label="Abfahrt">
              <input
                type="date"
                value={trip.meta.start}
                onChange={(e) => setMeta({ start: e.target.value })}
              />
            </Field>
            <Field label="Personen">
              <input
                type="number"
                min={1}
                value={trip.meta.crew}
                onChange={(e) => setMeta({ crew: Number(e.target.value) })}
              />
            </Field>
            {/*
              Die Währung stand im Modell und in keinem Formular: eine Schweizer
              Reise zeigte überall €, während die Zahlen daneben Franken waren.
              Der Code selbst ist eine Angabe der Reise und kein Merkmal des
              Fahrzeugs - derselbe Wagen fährt durch Länder, die verschieden
              zahlen.
            */}
            <Field label="Währung">
              <input
                value={trip.meta.currency}
                maxLength={3}
                placeholder="EUR"
                onChange={(e) => setMeta({ currency: waehrungscode(e.target.value) })}
              />
            </Field>
            <Field label={`Kraftstoffpreis je ${einheit}`}>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step={0.01}
                value={trip.meta.fuelPrice}
                onChange={(e) => setMeta({ fuelPrice: Number(e.target.value) })}
              />
            </Field>
          </div>
        )}
      </div>

      <VehicleEditor />

      <div className="block">
        <h4>Alles mitnehmen</h4>
        <p>
          Der Export enthält Route, Tagebuch, Ausgaben, Checklisten und alle Fotos in einer einzigen
          JSON-Datei. Sie ist das Sicherungsband: unabhängig vom Server, unabhängig von diesem
          Gerät.
        </p>
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf"
            onClick={() => void exportieren()}
            disabled={laeuft}
          >
            {packt ? (
              <>
                <Loader2 size={14} className="dreht" />{' '}
                {packt.gesamt > 0 ? `Foto ${packt.fertig} von ${packt.gesamt} …` : 'Export läuft …'}
              </>
            ) : (
              <>
                <Download size={14} /> Export als JSON
              </>
            )}
          </button>
          {/* Der Import ersetzt den ganzen Bestand: das ist der grösste
              denkbare Schreibvorgang und braucht folglich das Schreibrecht. */}
          {darfSchreiben && (
            <>
              <button
                type="button"
                className="knopf still"
                onClick={() => dateiFeld.current?.click()}
                disabled={laeuft}
              >
                <Upload size={14} /> Import
              </button>
              <input
                ref={dateiFeld}
                type="file"
                accept="application/json,.json"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  e.target.value = ''
                  if (file) void importieren(file)
                }}
              />
            </>
          )}
        </div>
        <p className="abschluss">
          Der Import versteht auch die Exportdatei des ursprünglichen Einzeldatei-Prototyps. Mit
          vielen Fotos wird die Datei gross: geschrieben wird sie stückweise und gelingt auch auf
          dem Telefon, gelesen werden muss sie am Stück — dafür ist ein Rechner die sicherere Wahl.
        </p>
        {meldung && <p className="hinweis">{meldung}</p>}
        {fehler && <p className="hinweis">{fehler}</p>}
      </div>

      {/*
        Der Export für die Navigation steht ausdrücklich hinter dem JSON-Export
        und nicht darin: die JSON-Datei ist die Sicherung und kommt hier wieder
        herein, GPX und KML gehen in fremde Programme und nie zurück. Zwei
        Knöpfe nebeneinander, die das eine tun und das andere nicht, wären die
        häufigste Art, sich die falsche Datei zu greifen.
      */}
      <NavExport />

      <div className="block">
        <h4>Datenmodell</h4>
        <pre>{`trip     = { meta: { title, vehicle, crew, start, consumption,
                     fuelPrice, currency },
             vehicle?: { name, fuel, consumption, tank, surcharge, sign },
             stages: [stage] }

stage    = { id, day, from, to, c1: [lat, lon], c2: [lat, lon],
             km, h, land, type, camping: [{ name, price, note, at?: [lat, lon] }],
             highlights: [string], night: [string], tip, optional?, skip?,
             route?: { geometry: [[lat, lon]], distanceKm, durationH,
                       computedAt, from: [lat, lon], to: [lat, lon] } }

diary    = { stageId, done, date, odo, realKm, weather }
post     = { id, stageId, authorId, authorName, createdAt, updatedAt, text }
expense  = { id, category, amount, note, stageId, date }
checks   = { vignettes: [{ country, price, note, done }],
             kit: [{ name, done }] }
photo    = { id, stageId, mime, size, sha256, createdAt }`}</pre>
        <p className="abschluss">
          Jede dieser Entitäten ist ein eigenes Dokument. Der Abgleich mit dem Server läuft für alle
          über denselben Weg: lokale Änderung, Revision, Konfliktauflösung nach Zeitstempel.
        </p>
      </div>

      <div className="block">
        <h4>Konto</h4>
        <p>
          {status === 'ready' && displayName
            ? `Angemeldet als ${displayName}.`
            : status === 'offline'
              ? 'Offline. Die App arbeitet mit dem lokalen Bestand weiter, der Abgleich ruht bis zur nächsten Verbindung.'
              : 'Nicht angemeldet.'}
          {config?.version && ` Serverfassung ${config.version}.`}
        </p>
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf gefahr"
            onClick={() => void abmelden()}
            disabled={laeuft}
          >
            {gleichtAb ? (
              <>
                <Loader2 size={14} className="dreht" /> Letzter Abgleich läuft …
              </>
            ) : (
              <>
                <LogOut size={14} /> Abmelden
              </>
            )}
          </button>
        </div>
        <p className="abschluss">
          Das Abmelden löscht den lokalen Bestand aller Reisen auf diesem Gerät, nicht nur den der
          offenen. Alles, was abgeglichen ist, kommt bei der nächsten Anmeldung zurück; was noch
          wartet, ist danach weg. Im Zweifel vorher exportieren.
          {arbeit.offen > 0 &&
            ` Zurzeit ${arbeit.offen === 1 ? 'wartet 1 Änderung' : `warten ${arbeit.offen} Änderungen`} auf den Abgleich.`}
          {arbeit.abgelehnt > 0 &&
            ` ${arbeit.abgelehnt === 1 ? '1 Foto liegt' : `${arbeit.abgelehnt} Fotos liegen`} nur auf diesem Gerät: der Server hat ${arbeit.abgelehnt === 1 ? 'es' : 'sie'} dauerhaft abgelehnt.`}
        </p>
      </div>
    </section>
  )
}
